import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PortalShell } from "@/components/AppHeader";
import { StarBadge } from "@/components/FeedbackForms";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { SpeedingBadge, SpeedingEventsTable } from "@/pages/admin/FeedbackAdmin";

const pct = (v) => (v == null ? "—" : `${v}%`);

const Stat = ({ label, value, sub, testId }) => (
  <div className="lux-card p-4 sm:p-5"><p className="lux-eyebrow !text-[0.6rem]">{label}</p><p data-testid={testId} className="mt-2 font-serif text-2xl text-amber-50 sm:text-3xl">{value}</p>{sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}</div>
);

const DriverStats = ({ s }) => (
  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
    <Stat label="Customer rating" value={<StarBadge rating={s.customer_rating} count={s.ratings_count} />} testId="stat-customer-rating" />
    <Stat label="Acceptance" value={pct(s.acceptance_rate)} sub={`${s.accepted_30d} of ${s.offers_received_30d} offers · 30 days`} testId="stat-acceptance" />
    <Stat label="Completion" value={pct(s.completion_rate)} sub={`${s.completed} of ${s.accepted} accepted`} testId="stat-completion" />
    <Stat label="On time" value={pct(s.on_time_rate)} sub={`${s.on_time} of ${s.on_time_measured} pickups · ETA + 3 min`} testId="stat-on-time" />
    <Stat label="Quality" value={pct(s.quality_rate)} sub="Vehicle, cleanliness, professionalism" testId="stat-quality" />
    <Stat label="Drives this month" value={s.drives_this_month} testId="stat-drives-month" />
    <Stat label="Funds this month" value={money(s.funds_this_month)} testId="stat-funds-month" />
    <Stat label="Complaints" value={s.complaints_count} sub="Not dismissed" testId="stat-complaints" />
  </div>
);

const CustomerStats = ({ s }) => (
  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
    <Stat label="Rating from drivers" value={<StarBadge rating={s.average_rating} count={s.ratings_count} />} testId="stat-average-rating" />
    <Stat label="Trips this month" value={s.trips_this_month} testId="stat-trips-month" />
    <Stat label="Completion" value={pct(s.completion_rate)} sub={`${s.completed} of ${s.booked} booked`} testId="stat-completion" />
    <Stat label="Complaints" value={s.complaints_count} sub="Not dismissed" testId="stat-complaints" />
  </div>
);

const List = ({ title, rows, testId, render }) => (
  <div className="lux-card p-5 sm:p-6" data-testid={testId}>
    <p className="lux-eyebrow mb-3">{title}</p>
    {rows.length === 0 && <p className="text-sm text-zinc-500">Nothing yet.</p>}
    {rows.map((r) => <div key={r.id} className="border-t border-white/5 py-3 text-sm first:border-0">{render(r)}</div>)}
  </div>
);

const SpeedingCard = ({ sp }) => (
  <div className={`lux-card space-y-4 p-5 sm:p-6 ${sp.record_this_month ? "border-red-500/40" : ""}`} data-testid="my-speeding">
    <div className="flex flex-wrap items-center gap-2">
      <p className="lux-eyebrow">Speeding</p>
      {sp.record_this_month ? <SpeedingBadge testId="my-speeding-record-badge" /> : <span data-testid="my-speeding-status" className="text-xs text-zinc-500">{sp.speeding_record ? `Record on file (${sp.record_months.join(", ")})` : "No speeding record"}</span>}
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div><p className="text-xs text-zinc-500">This month</p><p data-testid="my-speeding-month" className={`font-serif text-2xl ${sp.this_month ? "text-red-300" : "text-amber-50"}`}>{sp.this_month}</p></div>
      <div><p className="text-xs text-zinc-500">All time</p><p data-testid="my-speeding-all" className="font-serif text-2xl text-amber-50">{sp.all_time}</p></div>
    </div>
    <p className="text-xs text-zinc-500">4 or more violations in a month puts a speeding record on your file.</p>
    {sp.events.length ? <div className="overflow-x-auto"><SpeedingEventsTable events={sp.events} testId="my-speeding-events" /></div> : <p data-testid="my-speeding-none" className="text-sm text-zinc-500">No speeding violations — keep it up.</p>}
  </div>
);

export default function MyRatings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [comments, setComments] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [err, setErr] = useState(null);
  useEffect(() => {
    const fail = (e) => setErr(errMsg(e));
    api.get("/me/ratings").then(({ data }) => setS(data)).catch(fail);
    api.get("/me/comments").then(({ data }) => setComments(data)).catch(fail);
    api.get("/me/complaints").then(({ data }) => setComplaints(data)).catch(fail);
  }, []);
  return (
    <PortalShell>
      <p className="lux-eyebrow">Your file</p>
      <h1 className="mt-2 text-3xl font-semibold text-amber-50 sm:text-4xl">Ratings</h1>
      {err && <p data-testid="ratings-error" className="mt-6 text-sm text-amber-500">{err}</p>}
      {!s && !err && <p className="mt-6 text-sm text-zinc-500">Loading…</p>}
      {s?.on_hold && <p data-testid="ratings-on-hold" className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">Your account is on hold — please contact customer service at 214-506-2525</p>}
      {s && <div className="mt-8">{user.role === "driver" ? <DriverStats s={s} /> : <CustomerStats s={s} />}</div>}
      {s?.speeding && user.role === "driver" && <div className="mt-6"><SpeedingCard sp={s.speeding} /></div>}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <List title="Comments about you" rows={comments} testId="my-comments" render={(c) => (
          <><p className="text-zinc-200">“{c.text}”</p><p className="mt-1 text-xs text-zinc-500">{c.author_name} · trip on {fmtDate(c.trip_date)} · <Link to={`/trips/${c.trip_id}/feedback`} className="text-amber-300">feedback</Link></p></>
        )} />
        <List title="Complaints" rows={complaints} testId="my-complaints" render={(c) => (
          <><p className="text-zinc-200"><span className="text-amber-200">{c.category}</span> · {c.status}</p><p className="mt-1 text-zinc-400">{c.description}</p><p className="mt-1 text-xs text-zinc-500">{c.author_name} · trip on {fmtDate(c.trip_date)}</p></>
        )} />
      </div>
    </PortalShell>
  );
}
